// src/pages/appointments/edit/EditAppointmentLocation.jsx
import React from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import TopBarCenter from "@/sot/primitives/TopBarCenter.jsx";
import Button from "@/components/ui/Button.jsx";
import Card from "@/components/ui/Card.jsx";
import Checkbox from "@/components/ui/Checkbox.jsx";
import { resolveDoctorBookingEntryState } from "@/services/doctorBookingMode.js";
import { supabase } from "@/services/supabaseClient";
import { useLanguage } from "@/contexts/LanguageContext.jsx";

function splitCsvLine(line = "") {
  const out = [];
  let cur = "";
  let q = false;

  for (let i = 0; i < line.length; i++) {
    const ch = line[i];

    if (ch === '"' && line[i + 1] === '"') {
      cur += '"';
      i++;
      continue;
    }
    if (ch === '"') {
      q = !q;
      continue;
    }
    if (ch === "," && !q) {
      out.push(cur);
      cur = "";
      continue;
    }
    cur += ch;
  }
  out.push(cur);
  return out.map((s) => String(s).trim());
}

function pick(obj, keys) {
  for (const k of keys) {
    const v = obj?.[k];
    if (v != null && String(v).trim() !== "") return v;
  }
  return null;
}

function toNum(v) {
  const n = Number(String(v ?? "").trim());
  return Number.isFinite(n) ? n : null;
}

function titleCase(s = "") {
  return String(s)
    .trim()
    .split(/\s+/)
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : ""))
    .join(" ");
}

function looksLikeHospitalOrClinic(row) {
  const name = String(pick(row, ["name", "Name", "facility_name", "Facility", "facility"]) || "").toLowerCase();
  const type = String(pick(row, ["type", "Type", "category", "Category", "facility_type"]) || "").toLowerCase();

  const hay = `${name} ${type}`;
  const isHospital = hay.includes("hospital");
  const isClinic =
    hay.includes("clinic") ||
    hay.includes("health centre") ||
    hay.includes("health center") ||
    hay.includes("medical centre") ||
    hay.includes("medical center");
  const isPharmacy = hay.includes("pharmacy") || hay.includes("chemist");

  return (isHospital || isClinic) && !isPharmacy;
}

function compactAddress(parts = []) {
  return parts
    .map((part) => String(part || "").trim())
    .filter(Boolean)
    .join(", ");
}

function dedupeFacilities(items = []) {
  const seen = new Set();
  return items.filter((item) => {
    const key = String(
      item?.id ||
        `${String(item?.name || "").trim().toLowerCase()}::${String(item?.address || "").trim().toLowerCase()}`
    );
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function buildRestoredFacilityOption(restored) {
  const facility = restored?.facility || null;
  const id = String(restored?.facilityId || facility?.id || restored?.practice_id || "").trim();
  const name = String(restored?.facilityName || facility?.name || restored?.facility_name || "").trim();
  const address = String(
    facility?.address ||
      compactAddress([facility?.city, facility?.country])
  ).trim();

  if (!id && !name && !address) return null;

  return {
    id: id || `restored:${name || "facility"}`,
    name: titleCase(name || "Facility"),
    address: titleCase(address),
    lat: facility?.lat ?? null,
    lng: facility?.lng ?? null,
    raw: facility || null,
  };
}

async function loadDoctorFacilityOptions(rawDoctorId, restored) {
  const doctorId = String(rawDoctorId || "").trim();
  const restoredFacility = buildRestoredFacilityOption(restored);
  if (!doctorId) return restoredFacility ? [restoredFacility] : [];

  const [{ data: profilesByUserId }, { data: profilesByProfileId }] = await Promise.all([
    supabase.from("doctor_profiles").select("id,user_id").eq("user_id", doctorId),
    supabase.from("doctor_profiles").select("id,user_id").eq("id", doctorId),
  ]);

  const lookupIds = Array.from(
    new Set(
      [
        doctorId,
        ...(Array.isArray(profilesByUserId)
          ? profilesByUserId.flatMap((row) => [row?.id, row?.user_id])
          : []),
        ...(Array.isArray(profilesByProfileId)
          ? profilesByProfileId.flatMap((row) => [row?.id, row?.user_id])
          : []),
      ]
        .map((value) => String(value || "").trim())
        .filter(Boolean)
    )
  );

  const [{ data: consultancies }, { data: legacyLocations }] = await Promise.all([
    supabase
      .from("doctor_consultancies")
      .select("id,doctor_id,facility_name,facility_address,facility_city,facility_country,is_primary,sort_order,updated_at")
      .in("doctor_id", lookupIds)
      .order("is_primary", { ascending: false })
      .order("sort_order", { ascending: true })
      .order("updated_at", { ascending: false }),
    supabase
      .from("doctor_consulting_locations")
      .select("id,doctor_id,facility_name,street_address,city,country,updated_at")
      .in("doctor_id", lookupIds)
      .order("updated_at", { ascending: false }),
  ]);

  const hasConsultancies = Array.isArray(consultancies) && consultancies.length > 0;
  const sourceRows = hasConsultancies
    ? consultancies
    : Array.isArray(legacyLocations)
      ? legacyLocations
      : [];

  const mapped = sourceRows.map((row) => ({
    id: String(row?.id || "").trim(),
    name: titleCase(String(row?.facility_name || "Facility").trim()),
    address: titleCase(
      compactAddress(
        hasConsultancies
          ? [row?.facility_address, row?.facility_city, row?.facility_country]
          : [row?.street_address, row?.city, row?.country]
      )
    ),
    lat: null,
    lng: null,
    raw: row,
  }));

  return dedupeFacilities(restoredFacility ? [restoredFacility, ...mapped] : mapped);
}

export default function EditAppointmentLocation() {
  const nav = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const { t } = useLanguage();

  const restored = location.state || null;
  const actorRole = restored?.actorRole === "doctor" ? "doctor" : "patient";
  const currentMode =
    restored?.previousMode ||
    restored?.previous_mode ||
    restored?.mode ||
    restored?.visitMode ||
    (String(restored?.consultation_type || "").trim().toLowerCase() === "private" ? "evisit" : "inperson");
  const currentConsultationType =
    restored?.previousConsultationType ||
    restored?.previous_consultation_type ||
    restored?.consultation_type ||
    (currentMode === "evisit" ? "private" : "moh");
  const rawDoctorId = String(
    restored?.doctor?.id ||
      restored?.doctor?.user_id ||
      restored?.doctorId ||
      restored?.doctor_id ||
      restored?.docId ||
      ""
  ).trim();

  const [query, setQuery] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");
  const [modeAccessState, setModeAccessState] = React.useState({
    loading: false,
    evisitEnabled: true,
    hasInPersonOption: true,
  });

  const [facilities, setFacilities] = React.useState([]);

  // mode: "inperson" | "evisit"
  const initialMode =
    restored?.mode ||
    restored?.visitMode ||
    (restored?.isEvisit ? "evisit" : "inperson");

  const [mode, setMode] = React.useState(initialMode || "inperson");
  const [selectedId, setSelectedId] = React.useState(
    restored?.facilityId || restored?.facility?.id || restored?.practice_id || null
  );

  React.useEffect(() => {
    let alive = true;

    async function load() {
      setLoading(true);
      setError("");

      try {
        if (rawDoctorId) {
          const scopedFacilities = await loadDoctorFacilityOptions(rawDoctorId, restored);
          if (!alive) return;
          setFacilities(scopedFacilities);
          return;
        }

        const res = await fetch("/data/health_facilities_sadc.csv", { cache: "no-store" });
        if (!res.ok) {
          throw new Error(t("appointments.editLocation.errors.loadCsv", "Failed to load facilities CSV"));
        }

        const text = await res.text();
        const lines = text
          .split(/\r?\n/)
          .map((l) => l.trim())
          .filter(Boolean);

        if (lines.length < 2) {
          throw new Error(t("appointments.editLocation.errors.emptyCsv", "Facilities CSV is empty"));
        }

        const headers = splitCsvLine(lines[0]);
        const rows = lines.slice(1).map((l) => {
          const cols = splitCsvLine(l);
          const obj = {};
          headers.forEach((h, i) => (obj[h] = cols[i]));
          return obj;
        });

        const mapped = rows
          .filter(looksLikeHospitalOrClinic)
          .map((r, idx) => {
            const latRaw = pick(r, ["lat", "latitude", "Latitude", "LAT", "y", "Y"]);
            const lngRaw = pick(r, ["lng", "lon", "longitude", "Longitude", "LON", "x", "X"]);
            const lat = toNum(latRaw);
            const lng = toNum(lngRaw);

            const name =
              pick(r, ["name", "Name", "facility_name", "Facility", "facility", "health_facility"]) ||
              t("appointments.editLocation.facilityFallback", "Facility");

            const address =
              pick(r, ["address", "Address", "facility_address", "location", "Location", "town"]) || "";

            return {
              id: `fac-${idx}`,
              name: titleCase(String(name).trim()),
              address: titleCase(String(address || "").trim()),
              lat,
              lng,
              raw: r,
            };
          });

        if (!alive) return;
        setFacilities(mapped);
      } catch (e) {
        if (!alive) return;
        setError(
          e?.message ||
            t("appointments.editLocation.errors.load", "Failed to load facilities")
        );
        setFacilities([]);
      } finally {
        if (!alive) return;
        setLoading(false);
      }
    }

    load();
    return () => {
      alive = false;
    };
  }, [actorRole, rawDoctorId, restored, t]);

  React.useEffect(() => {
    let alive = true;

    async function hydrateModeAccess() {
      if (!rawDoctorId) return;

      setModeAccessState((prev) => ({ ...prev, loading: true }));
      try {
        const selectedFacility =
          facilities.find((option) => option.id === selectedId) ||
          restored?.facility ||
          null;
        const resolved = await resolveDoctorBookingEntryState({
          doctorId: rawDoctorId,
          doctor: restored?.doctor || null,
          requestedMode: mode,
          consultationType: restored?.consultation_type || null,
          facilityId: selectedId || restored?.facilityId || restored?.practice_id || null,
          facilityName: restored?.facilityName || restored?.facility?.name || "",
          facility: selectedFacility,
        });
        if (!alive) return;

        setModeAccessState({
          loading: false,
          evisitEnabled: resolved.evisitEnabled,
          hasInPersonOption: resolved.hasInPersonOption,
        });

        if (resolved.mode && resolved.mode !== mode) {
          setMode(resolved.mode);
        }
        if (resolved.mode === "inperson" && !selectedId && resolved.facilityId) {
          setSelectedId(resolved.facilityId);
        }
      } catch {
        if (!alive) return;
        setModeAccessState((prev) => ({ ...prev, loading: false }));
      }
    }

    hydrateModeAccess();
    return () => {
      alive = false;
    };
  }, [
    facilities,
    mode,
    rawDoctorId,
    restored,
    selectedId,
  ]);

  const filtered = React.useMemo(() => {
    const q = String(query || "").trim().toLowerCase();
    let list = Array.isArray(facilities) ? facilities : [];
    if (q) list = list.filter((f) => `${f.name} ${f.address}`.toLowerCase().includes(q));
    return list.slice(0, 250);
  }, [facilities, query]);

  const canConfirm = mode === "evisit" || !!selectedId;
  const useCompactDoctorFooter = !!rawDoctorId && Array.isArray(facilities) && facilities.length > 1;
  const evisitDisabled = !modeAccessState.loading && !modeAccessState.evisitEnabled;
  const inpersonDisabled = !modeAccessState.loading && !modeAccessState.hasInPersonOption;

  return (
    <div className="min-h-[100dvh] bg-[rgb(var(--clr-surface-container-low))]" data-hide-tabbar="true">
      <div className="afrimed-rail afrimed-rail--comfortable pt-4 pb-24">
        <TopBarCenter
          title={t("appointments.editLocation.title", "Change location")}
          close
          onBack={() => nav(-1)}
        />

        {/* Search */}
        <div className="mt-4">
          <div className="flex items-center gap-3 h-12 rounded-full bg-transparent px-1">
            <img src="/icons/doctor/search.svg" alt="" className="h-6 w-6 opacity-70" draggable="false" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t(
                "appointments.editLocation.searchPlaceholder",
                "Search hospitals or clinics"
              )}
              className="
                flex-1 bg-transparent outline-none
                text-body-large text-on-surface
                placeholder:text-on-surface-var
              "
            />
          </div>
          <div className="mt-3 h-px w-full bg-[rgba(91,107,134,0.20)]" />
          {/* extra air below divider */}
          <div className="h-7" />
        </div>

        {/* List (scroll area) */}
        <div className={useCompactDoctorFooter ? "mt-0 overflow-visible pb-4" : "mt-0 flex-1 overflow-auto pb-28"}>
          {loading ? (
            <div className="mt-2 text-body-medium text-on-surface-var">
              {t("common.loading", "Loading...")}
            </div>
          ) : null}
          {!loading && error ? <div className="mt-2 text-body-medium text-[crimson]">{error}</div> : null}

          {!loading && !error ? (
            <div className="space-y-3">
              {/* Virtual option (separate card above list) */}
              <Card className="overflow-hidden">
                <div
                  role="button"
                  tabIndex={0}
                  className="w-full text-left"
                  onClick={() => {
                    if (evisitDisabled) return;
                    setMode("evisit");
                    setSelectedId(null);
                  }}
                  onKeyDown={(e) => {
                    if (evisitDisabled) return;
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setMode("evisit");
                      setSelectedId(null);
                    }
                  }}
                >
                  <div className="px-4 py-4 flex items-center gap-4">
                    <img
                      src="/icons/appointments/video_call.svg"
                      alt=""
                      className="h-7 w-7 opacity-90"
                      draggable="false"
                    />

                    <div className="flex-1 min-w-0">
                      <div className="text-body-large text-on-surface truncate">
                        {t("appointments.edit.mode.eVisit", "Virtual")}
                      </div>
                      <div className="text-body-medium text-on-surface-var truncate mt-0.5">
                        {t(
                          "appointments.editLocation.evisitSubtitle",
                          "Switch this appointment to a video call virtual visit"
                        )}
                      </div>
                    </div>

                    <div className="shrink-0">
                      <Checkbox
                        checked={mode === "evisit"}
                        onChange={(next) => {
                          if (evisitDisabled) return;
                          if (!next) {
                            // If they untick, return to in-person (but no facility selected yet)
                            setMode("inperson");
                            return;
                          }
                          setMode("evisit");
                          setSelectedId(null);
                        }}
                        aria-label={t("appointments.editLocation.selectEvisit", "Select virtual consultation")}
                      />
                    </div>
                  </div>
                </div>
              </Card>

              {evisitDisabled ? (
                <div className="px-1 text-body-medium text-on-surface-var">
                  {t(
                    "appointments.editLocation.evisitUnavailable",
                    "Virtual consultations are not currently enabled for this doctor, so bookings stay aligned to in-person consult locations."
                  )}
                </div>
              ) : null}

              {/* Facilities list card */}
              <Card className="overflow-hidden">
                {filtered.map((f, idx) => {
                  const checked = mode === "inperson" && selectedId === f.id;

                  return (
                    <div key={f.id}>
                      <div
                        role="button"
                        tabIndex={0}
                        className="w-full text-left"
                        onClick={() => {
                          if (inpersonDisabled) return;
                          setMode("inperson");
                          setSelectedId(f.id);
                        }}
                        onKeyDown={(e) => {
                          if (inpersonDisabled) return;
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            setMode("inperson");
                            setSelectedId(f.id);
                          }
                        }}
                      >
                        <div className="px-4 py-4 flex items-center gap-4">
                          <img
                            src="/icons/appointments/hospital_Fill.svg"
                            alt=""
                            className="h-7 w-7 opacity-90"
                            draggable="false"
                          />

                          <div className="flex-1 min-w-0">
                            <div className="text-body-large text-on-surface truncate">{f.name}</div>
                            {f.address ? (
                              <div className="text-body-medium text-on-surface-var truncate mt-0.5">
                                {f.address}
                              </div>
                            ) : null}
                          </div>

                          <div className="shrink-0">
                            <Checkbox
                              checked={checked}
                              onChange={(next) => {
                                if (inpersonDisabled) return;
                                if (!next) {
                                  // untick => no facility selected (still in-person mode)
                                  setMode("inperson");
                                  setSelectedId(null);
                                  return;
                                }
                                setMode("inperson");
                                setSelectedId(f.id);
                              }}
                              aria-label={t("appointments.editLocation.selectFacility", "Select {name}", {
                                name: f.name,
                              })}
                            />
                          </div>
                        </div>
                      </div>

                      {/* short divider */}
                      {idx !== filtered.length - 1 ? (
                        <div
                          className="h-px ml-[15px] mr-[0px]"
                          style={{ background: "var(--Schemes-Outline-Variant,#E6E7EB)" }}
                        />
                      ) : null}
                    </div>
                  );
                })}

                {filtered.length === 0 ? (
                  <div className="px-4 py-6 text-body-medium text-on-surface-var">
                    {t("appointments.editLocation.empty", "No facilities found.")}
                  </div>
                ) : null}
              </Card>

              {inpersonDisabled ? (
                <div className="px-1 text-body-medium text-on-surface-var">
                  {t(
                    "appointments.editLocation.inpersonUnavailable",
                    "This doctor does not currently have an in-person consult location available for booking."
                  )}
                </div>
              ) : null}
            </div>
          ) : null}
        </div>

  {/* Bottom CTAs */}
  <div
    className={
      useCompactDoctorFooter
        ? "mt-4 mb-[env(safe-area-inset-bottom)]"
        : "sticky bottom-0 md:bottom-[0px] z-30 pointer-events-none"
    }
  >
    <div className="afrimed-rail pt-0 pb-0">
      <div
        className={
          useCompactDoctorFooter
            ? "pt-0 pb-0 bg-transparent shadow-none"
            : "w-full bg-[rgb(var(--clr-surface-cont-highest))] pt-2 pb-3 mb-[env(safe-area-inset-bottom)] rounded-none shadow-[0_-8px_24px_rgba(15,23,42,0.08)]"
        }
      >
        <div
          className={
            useCompactDoctorFooter
              ? "flex gap-3 w-full md:max-w-[560px] mx-auto px-0 md:px-0"
              : "flex gap-3 w-full md:max-w-[560px] mx-auto px-3 md:px-0 pointer-events-auto"
          }
        >
          <Button variant="ghost" className="flex-1 h-12" onClick={() => nav(-1)}>
            {t("common.cancel", "Cancel")}
          </Button>

          <Button
            className="flex-1 h-12"
            disabled={!canConfirm}
            onClick={async () => {
              const restoredFacility = buildRestoredFacilityOption(restored);
              const chosen =
                facilities.find((x) => x.id === selectedId) ||
                (restoredFacility && restoredFacility.id === selectedId ? restoredFacility : null);
              const nextFacilityId =
                mode === "inperson"
                  ? selectedId || restored?.practice_id || restoredFacility?.id || null
                  : null;
              const nextFacilityName =
                mode === "inperson"
                  ? chosen?.name || restored?.facilityName || restored?.facility?.name || ""
                  : "";
              const nextFacility =
                mode === "inperson"
                  ? chosen
                    ? {
                        id: nextFacilityId,
                        name: chosen.name,
                        address: chosen.address,
                        lat: chosen.lat,
                        lng: chosen.lng,
                      }
                    : restored?.facility || null
                  : null;
              const nextConsultationType =
                mode === "evisit"
                  ? "private"
                  : String(restored?.consultation_type || "").toLowerCase() === "private"
                    ? "moh"
                    : restored?.consultation_type || "moh";
              const resolvedModeState = await resolveDoctorBookingEntryState({
                doctorId: rawDoctorId,
                doctor: restored?.doctor || null,
                requestedMode: mode,
                consultationType: nextConsultationType,
                facilityId: nextFacilityId,
                facilityName: nextFacilityName,
                facility: nextFacility,
              });

              const next = {
                ...(restored || {}),
                mode: resolvedModeState.mode,
                previousMode: currentMode,
                previousConsultationType: currentConsultationType,
                facilityId: resolvedModeState.facilityId,
                facilityName: resolvedModeState.facilityName,
                facility: resolvedModeState.facility,
                practice_id: resolvedModeState.facilityId,
                consultation_type:
                  resolvedModeState.mode === "evisit" ? "private" : nextConsultationType,
              };

              try {
                sessionStorage.setItem(
                  `afrimed:appointment:${String(id)}`,
                  JSON.stringify(next)
                );
                sessionStorage.setItem("afrimed:appointment:last", JSON.stringify(next));
                sessionStorage.setItem(`afrimed:doctor_appt:${String(id)}`, JSON.stringify(next));
              } catch {}

              // ✅ If we're coming from a rebook flow, go to AppointmentBook next.
              const nextDoctorId =
                next?.doctor?.id || restored?.doctor?.id || restored?.doctorId || restored?.docId || null;

              if (restored?.__flow === "rebook" && nextDoctorId) {
                nav(`/appointments/book/${nextDoctorId}`, {
                  replace: true,
                  state: {
                    doctor: restored?.doctor || null,
                    // carry chosen location + mode into booking
                    mode: next.mode,
                    facilityId: next.facilityId,
                    facilityName: next.facilityName,
                    facility: next.facility,
                    __flow: "rebook",
                    fromPastAppointmentId: restored?.fromPastAppointmentId || null,
                  },
                });
                return;
              }

              // default behaviour (non-rebook)
              nav(`/appointments/confirm/${id}`, {
                state: { ...next, __flow: "reschedule" },
              });
            }}
          >
            {t("appointments.edit.confirm", "Confirm")}
          </Button>
        </div>
      </div>
    </div>
  </div>

</div>
</div>
);
}
